import React, { useCallback } from "react";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { AnnualMultiTrip, Europe, Worldwide } from "@/config/app-constant";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { STCountryArray } from "@/types/QuestionPayloadType";
import { travelFromCountryData } from "@/utils/question-location-from.utils";
import {
  findAnswerById,
  findAnswerForEuropeId,
  findAnswerWorldwideById,
} from "@/utils/question-location-to.utils";
import {
  handleScrollToRef,
  handleScrollToRefAnyoneVisiting,
  smoothScrollToRef,
} from "@/utils/scrollUtils";

type QuestionState = {
  fromCountrySelected: string | null;
  toCountrySelected: string | null;
  valueSelected: string | null; // for amt
  countryArray: []; // for amt
};

type UseLocationToActionsProps = {
  setQuestionState: React.Dispatch<React.SetStateAction<QuestionState>>;

  setSelectedAMTEuropeWorldwide: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  setSelectedUKModal: React.Dispatch<React.SetStateAction<number | null>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFromCountryCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  modifyPayloadToLocation: (
    fromLocation?: string | undefined,
    toLocation?: string | undefined,
    area?: string | undefined,
    stCountryList?: STCountryArray
  ) => void;
  fromCountryCode: string | undefined;
  questionState: QuestionState;
  toCountrySelected: string | null;
  selectedpolicyType: string | undefined;
  toLocationCountryDetails: STCountryArray | undefined;
  actionButtonRef: React.RefObject<HTMLDivElement | null>;
  worldwideHeaderRef: React.RefObject<HTMLDivElement | null>;
  europeHeaderRef: React.RefObject<HTMLDivElement | null>;
  isStickyFooter: boolean;
};

export function useLocationToActions({
  setQuestionState,
  setSelectedAMTEuropeWorldwide,
  setSelectedUKModal,
  setFromCountryCode,
  setModalOpen,
  modifyPayloadToLocation,
  fromCountryCode,
  questionState,
  actionButtonRef,
  worldwideHeaderRef,
  europeHeaderRef,
  isStickyFooter,
  toCountrySelected,
  selectedpolicyType,
  toLocationCountryDetails,
}: UseLocationToActionsProps) {
  const { selected: stateToLocation, setSelectedState } = useQuestionsState();
  const { current } = useWizard();

  const selectedEurope = useCallback(
    (answerNo: number) => {
      const qtAnswer = findAnswerForEuropeId(answerNo)?.value;
      if (qtAnswer === stateToLocation) {
        setQuestionState({ ...questionState, valueSelected: null });
        setSelectedState(Europe);
        return;
      }
      setSelectedState(qtAnswer ?? "");
      modifyPayloadToLocation(fromCountryCode, undefined, qtAnswer ?? "");
    },
    [
      fromCountryCode,
      modifyPayloadToLocation,
      questionState,
      setQuestionState,
      setSelectedState,
      stateToLocation,
    ]
  );

  const selectedWorldWide = useCallback(
    (answerNo: number) => {
      const qtAnswer = findAnswerWorldwideById(answerNo)?.value;
      if (qtAnswer === stateToLocation) {
        setQuestionState({ ...questionState, valueSelected: null });
        setSelectedState(Worldwide);
        return;
      }
      setSelectedState(qtAnswer ?? "");
      modifyPayloadToLocation(undefined, qtAnswer ?? "");
      setSelectedState(qtAnswer ?? "");
      modifyPayloadToLocation(fromCountryCode, undefined, qtAnswer ?? "");
    },
    [
      fromCountryCode,
      modifyPayloadToLocation,
      questionState,
      setQuestionState,
      setSelectedState,
      stateToLocation,
    ]
  );

  const modifyModalButtonClick = useCallback(
    (buttonClick: boolean) => {
      setModalOpen((prev) => !prev);
      if (buttonClick) {
        if (current.uiElementControls?.locationFrom) {
          const element1 = document.getElementById("paragaph-test-id");
          if (element1)
            handleScrollToRefAnyoneVisiting(actionButtonRef, !isStickyFooter);
          else
            setTimeout(() => {
              const targetRef =
                toCountrySelected === Europe
                  ? worldwideHeaderRef
                  : europeHeaderRef;
              if (targetRef?.current) {
                smoothScrollToRef(targetRef);
              }
            }, 300);
        } else handleScrollToRef(actionButtonRef, !isStickyFooter);

        setSelectedUKModal(1);
        let selectedVal;
        const fromCountryAMT =
          current.brandSpecificParams?.fromCountryAMT ??
          current.brandSpecificParams?.fromCountry;
        if (!current.uiElementControls?.locationFrom) {
          selectedVal = fromCountryAMT as string;
        } else {
          selectedVal = Europe;
        }
        setSelectedAMTEuropeWorldwide(selectedVal);
        setSelectedState(selectedVal);
        modifyPayloadToLocation(fromCountryCode, undefined, selectedVal);
      } else if (
        !toCountrySelected ||
        toCountrySelected ===
          (current.brandSpecificParams?.fromCountry as string)
      ) {
        setSelectedUKModal(null);
        setSelectedAMTEuropeWorldwide(undefined);
        setSelectedState("");
        modifyPayloadToLocation(
          fromCountryCode,
          undefined,
          current.brandSpecificParams?.fromCountry as string
        );
      } else if (
        !toCountrySelected ||
        toCountrySelected ===
          (current.brandSpecificParams?.fromCountry as string) ||
        toCountrySelected === Europe
      ) {
        setSelectedUKModal(null);
      }
    },
    [
      actionButtonRef,
      europeHeaderRef,
      fromCountryCode,
      isStickyFooter,
      modifyPayloadToLocation,
      setModalOpen,
      setSelectedAMTEuropeWorldwide,
      setSelectedState,
      setSelectedUKModal,
      toCountrySelected,
      worldwideHeaderRef,
      current,
    ]
  );

  const selectedCountry = useCallback(
    (answerNo: number) => {
      const selectedCountryAMT =
        findAnswerById(
          answerNo,
          current.brandSpecificParams?.locationToCountries
        )?.value ?? "";
      const fromCountryAMT =
        current.brandSpecificParams?.fromCountryAMT ??
        current.brandSpecificParams?.fromCountry;

      if (selectedCountryAMT === toCountrySelected) {
        setSelectedUKModal(null);
        setSelectedAMTEuropeWorldwide(undefined);
        setQuestionState({
          ...questionState,
          toCountrySelected: null,
          valueSelected: null,
        });
        setSelectedState("");
        return;
      }
      if (
        selectedCountryAMT === fromCountryAMT &&
        !current.uiElementControls?.locationFrom
      ) {
        if (!toCountrySelected || toCountrySelected === fromCountryAMT) {
          setSelectedUKModal(null);
        }
        setModalOpen(true);
      } else if (
        selectedCountryAMT === Europe &&
        current.uiElementControls?.locationFrom
      ) {
        if (!toCountrySelected || toCountrySelected === Europe)
          setSelectedUKModal(null);
        setModalOpen(true);
      } else {
        setSelectedAMTEuropeWorldwide(selectedCountryAMT);
        setSelectedState(selectedCountryAMT);
        modifyPayloadToLocation(fromCountryCode, undefined, selectedCountryAMT);
      }
    },
    [
      fromCountryCode,
      modifyPayloadToLocation,
      questionState,
      setQuestionState,
      setSelectedAMTEuropeWorldwide,
      setSelectedState,
      toCountrySelected,
      setSelectedUKModal,
      setModalOpen,
      current,
    ]
  );

  const handleFromCountrySelection = useCallback(
    (from_country: string) => {
      const countryCode = travelFromCountryData.find(
        (country) => country.displayName === from_country
      );
      setFromCountryCode(countryCode?.code);
      let toLocation = stateToLocation;

      // To prevent setting 0 as a value for toLocation
      if (!toLocation) {
        toLocation = undefined;
      }

      if (selectedpolicyType === AnnualMultiTrip) {
        modifyPayloadToLocation(
          countryCode?.code as string,
          undefined,
          toLocation as string
        );
      } else {
        modifyPayloadToLocation(
          countryCode?.code as string,
          toLocation as string,
          undefined,
          toLocationCountryDetails
        );
      }
    },
    [
      modifyPayloadToLocation,
      selectedpolicyType,
      setFromCountryCode,
      stateToLocation,
      toLocationCountryDetails,
    ]
  );

  return {
    selectedEurope,
    selectedWorldWide,
    selectedCountry,
    modifyModalButtonClick,
    handleFromCountrySelection,
  };
}
